import { Directive, Input, OnDestroy, OnInit } from '@angular/core';
import { CardPage } from '../../../card/model/card-page';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
    createPageableByRouterParameters,
    Pageable,
    setDefaultWorkspaceSortingParameters,
} from '../../../shared/models/pageable';
import {
    Container,
    ContainerPage,
    ContainerType,
} from '../../../shared/models/container.types';
import { AuthenticationService } from '../../../authentication/services/authentication.service';
import {
    combineLatest,
    EMPTY,
    filter,
    merge,
    map,
    Observable,
    Subscription,
    switchMap,
    tap,
    catchError,
    withLatestFrom,
    shareReplay,
} from 'rxjs';
import { Card } from 'src/app/card/model/card-implementations/card';

@Directive()
export abstract class BaseLibraryComponent implements OnInit, OnDestroy {
    cardPage: CardPage = new CardPage();
    @Input() profilePicture: string;
    private subscriptions: Subscription[] = [];
    types: ContainerType[];

    protected constructor(
        protected authenticationService: AuthenticationService,
        protected route: ActivatedRoute,
        protected router: Router
    ) {}

    ngOnInit(): void {
        if (!this.authenticationService.isLoggedIn()) {
            console.error('There is an internal error.');
            this.authenticationService.signOut();
            return;
        }

        const routeState$ = combineLatest([
            this.route.params,
            this.route.queryParams,
        ]).pipe(
            map(([params, queryParams]) => ({ params, queryParams })),
            shareReplay({ bufferSize: 1, refCount: true })
        );
        let pendingContextUrlUpdate = false;

        this.subscriptions.push(
            merge(
                routeState$.pipe(
                    map(({ params, queryParams }) => ({
                        params,
                        queryParams,
                        isContextChange: false,
                    }))
                ),
                this.authenticationService.activeContextChanged$.pipe(
                    withLatestFrom(routeState$),
                    map(([, routeState]) => ({
                        ...routeState,
                        isContextChange: true,
                    }))
                )
            )
                .pipe(
                    tap(({ params }) => this.setTypes(params)),
                    filter(({ queryParams, isContextChange }) => {
                        if (
                            !isContextChange &&
                            pendingContextUrlUpdate &&
                            queryParams.page == 1
                        ) {
                            pendingContextUrlUpdate = false;
                            return false;
                        }
                        return true;
                    }),
                    switchMap(({ queryParams, isContextChange }) => {
                        if (isContextChange) {
                            pendingContextUrlUpdate = true;
                            this.router.navigate([], {
                                relativeTo: this.route,
                                queryParams: { page: 1 },
                                queryParamsHandling: 'merge',
                                replaceUrl: true,
                            });
                        }
                        return this.loadPage(queryParams, isContextChange);
                    })
                )
                .subscribe()
        );
    }

    private loadPage(params: Params, contextChange = false): Observable<void> {
        this.clearPage();
        this.cardPage.pageable = this.createPageable(params, contextChange);
        return this.getData().pipe(
            tap((containerPage: ContainerPage) => {
                this.cardPage.numberOfItems = containerPage.totalElements;
                this.cardPage.numberOfPages = containerPage.totalPages;
                this.populate(containerPage.values);
            }),
            map(() => undefined),
            catchError(() => EMPTY)
        );
    }

    private createPageable(params: Params, resetPage = false): Pageable {
        const pageable = createPageableByRouterParameters(
            this.cardPage.pageable ?? new Pageable(),
            params
        );
        if (resetPage) {
            pageable.page = 1;
        }
        this.cardPage.pageable = pageable;
        setDefaultWorkspaceSortingParameters(this.cardPage);
        return pageable;
    }

    private setTypes(params: Params): void {
        switch (params['type']) {
            case 'visual':
                this.types = [ContainerType.PROJECT_VISUAL];
                break;
            case 'motoric':
                this.types = [ContainerType.PROJECT_MOTORIC];
                break;
            case 'imageDb':
                this.types = [ContainerType.IMAGE_DB];
                break;
        }
    }

    private clearPage(): void {
        this.cardPage.itemCards = [];
        this.cardPage.numberOfItems = 0;
        this.cardPage.numberOfPages = 0;
    }

    populate(containers: Container[]): void {
        this.cardPage.itemCards = [];
        containers.forEach((container: Container) => {
            let card = this.convertToCard(container, this.profilePicture);
            this.cardPage.itemCards.push(card);
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((subscription) =>
            subscription.unsubscribe()
        );
    }

    abstract convertToCard(container: Container, picture: string): Card;

    abstract getData(): Observable<ContainerPage>;
}
